import { CheckboxGroupComponent, Module, STEP_TYPE } from '@abiglobalhealth/go-types'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useChoices } from '@/hooks/useChoices'
import { useModule } from '@/hooks/useModule'
import { useStep } from '@/hooks/useStep'

import { Checkbox } from './Checkbox'

export type CheckboxGroupProps = Omit<CheckboxGroupComponent, 'componentType'> & {
  tabIndex: number
}

export const CheckboxGroup = ({ id, textKey, options, tabIndex }: CheckboxGroupProps) => {
  const { t } = useTranslation()
  const { choices, addChoice } = useChoices()

  const { currentStep } = useStep()

  const initialValues = useMemo(
    () => choices.find((choice) => choice.stepId === currentStep?.id && choice.component.id === id)?.options?.map((option) => option.optionId) || [],
    [choices, currentStep, id]
  )

  const [selected, setSelected] = useState<string[]>(initialValues)

  const { currentModule } = useModule()

  useEffect(() => {
    if (currentStep?.type !== STEP_TYPE.CHOICES) return

    addChoice({
      date: new Date(),
      component: {
        id,
        key: textKey,
        text: t(textKey)
      },
      stepId: currentStep.id,
      moduleId: (currentModule as Module)?.id,
      options: selected.map((id) => {
        const option = options.find((option) => option.id === id)
        return {
          optionId: id,
          value: {
            key: option?.value as string,
            text: t(option?.textKey as string)
          }
        }
      })
    })
  }, [selected])

  return (
    <div data-testid={`${id}-container`} className="flex flex-col gap-4">
      <label className="font-bold">{t(textKey)}</label>

      <p>{t('experiencing_symptoms.description')}</p>

      {options.map((option, index) => {
        const elementTabIndex = Math.abs(tabIndex - options.length + index) + 1

        return (
          <Checkbox
            key={option.id}
            id={option.id}
            textKey={option.textKey}
            checked={selected.includes(option.id)}
            tabIndex={elementTabIndex}
            onChange={(e) => {
              if (e.target.checked) {
                setSelected([...selected, option.id])
              } else {
                setSelected(selected.filter((id) => id !== option.id))
              }
            }}
          />
        )
      })}
    </div>
  )
}
